"use client";

import { Box, CssBaseline, ThemeProvider } from "@mui/material";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { NextIntlClientProvider } from "next-intl";
import { usePathname } from "next/navigation";
import Navbar from "../_components/Navigation/Navbar";
import { BackdropLoader } from "../_components/_basics/BackdropLoader";
import { ProgressDialog } from "../_components/_basics/ProgressDialog";
import WheelFixWrapper from "../_components/_basics/WheelFixWrapper";
import { TimelineScrollProvider } from "../_contexts/Bookings/TimelineScrollProvider";
import { AreaProvider } from "../_contexts/Area/AreaContext";
import { ProgressProvider } from "../_contexts/ProgressProvider";
import { ToastProvider } from "../_contexts/ToastProvider";
import AuthProvider from "../auth/Provider";
import theme from "../styles/theme";
import ViewportUnits from "../_components/Viewport/ViewportUnits";

export default function AppWithProviders({
  locale,
  messages,
  children,
}: {
  locale: string;
  messages: Record<string, any>;
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const isExternalRoute = [
    "/login",
    "/forgot-password",
    "/request",
  ].some((segment) => pathname?.endsWith(segment));
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 60, // 1h – API data changes rarely
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  });

  // these two timezone and now need to be set to avoid build errors for ENVIRONMENT_FALLBACK
  const timeZone = process.env.NEXT_PUBLIC_DEFAULT_TZ || "Europe/Berlin";
  const now = new Date();

  return (
    <AuthProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <NextIntlClientProvider
            locale={locale}
            messages={messages}
            timeZone={timeZone}
            now={now}
          >
            <AreaProvider>
              <TimelineScrollProvider>
                <ProgressProvider>
                  <ProgressDialog />
                  <BackdropLoader />
                  <ToastProvider>
                    <WheelFixWrapper>
                      <CssBaseline />
                      <Box
                        className="min-h-screen-dvh"
                        sx={{
                          display: "flex",
                          backgroundColor: (theme) =>
                            theme.palette.background.default,
                        }}
                      >
                        <ViewportUnits />
                        {!isExternalRoute && <Navbar />}
                        {children}
                      </Box>
                    </WheelFixWrapper>
                  </ToastProvider>
                </ProgressProvider>
              </TimelineScrollProvider>
            </AreaProvider>
          </NextIntlClientProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </AuthProvider>
  );
}
